import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Heart, ArrowRight, ArrowsClockwise, Info,
  PauseCircle, PlugsConnected, Warning, ClockCountdown,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatLocal } from "@/lib/timezone";
import LeadStatusChip from "@/components/LeadStatusChip";

// Lead-centric drip-health overview. Companion to the
// enrolment-centric Send Forecast page — same payload, one row per
// LEAD bucketed by health state. See `/app/backend/routes/drip_health.py`
// for the SQL behind each bucket.

const BUCKETS = [
  { key: "no_active",   icon: PlugsConnected,  tone: "amber" },
  { key: "paused",      icon: PauseCircle,     tone: "blue" },
  { key: "lead_paused", icon: PauseCircle,     tone: "purple" },
  { key: "all_errored", icon: Warning,         tone: "red" },
  { key: "stale",       icon: ClockCountdown,  tone: "grey" },
];

const TONE_CLASSES = {
  amber:  "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
  blue:   "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  purple: "bg-[#F5F3FF] text-[#5B21B6] border-[#DDD6FE]",
  red:    "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  grey:   "bg-[#FAFAFA] text-[#525252] border-[#E5E5E5]",
};

// Friendly labels for the raw `crm_leads.pause_reason` values. Same
// mapping as LeadDetail.js so the operator sees consistent wording
// across the app. Falls back to the raw value when there's no mapping.
const PAUSE_REASON_LABELS = {
  invalid_email:                "Email bounced — bad address",
  invalid_email_inferred:       "Email bounced (inferred from history)",
  unsubscribed:                 "Unsubscribed",
  unsubscribed_inferred:        "Unsubscribed (inferred from history)",
  booking_click:                "Clicked booking CTA — awaiting meeting outcome",
  booking_click_inferred:       "Clicked booking CTA (inferred)",
  engagement_reply:             "Replied to a drip — pending human follow-up",
  do_not_contact_inferred:      "Do not contact — flagged by sales",
  historical_unknown_inferred:  "Paused historically — reason not recorded",
};
function friendlyPauseReason(raw) {
  if (!raw) return null;
  return PAUSE_REASON_LABELS[raw] || raw;
}

export default function DripHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("no_active");
  const [staleDays, setStaleDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/drip-health?stale_days=${staleDays}&limit_per_bucket=200`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load drip health");
    }
    setLoading(false);
  }, [staleDays]);

  useEffect(() => { load(); }, [load]);

  const bucket = data?.buckets?.[activeTab];
  const items = bucket?.items || [];

  return (
    <div className="p-6 max-w-screen-2xl mx-auto space-y-6" data-testid="drip-health-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <Heart size={28} weight="bold" /> Drip Health
          </h1>
          <p className="text-xs text-[#525252] mt-1.5 max-w-3xl leading-snug">
            Lead-centric view of who's <em>not</em> being nurtured right now. Five buckets
            surface the leads that have fallen out of automated drip — either because
            they have no active enrolment, an enrolment is paused, the lead itself is
            paused, every enrolment errored, or no email has been sent in a while.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">
            Stale window
          </label>
          <select
            value={staleDays}
            onChange={e => setStaleDays(parseInt(e.target.value, 10))}
            className="border border-[#E5E5E5] px-2 py-1 text-xs"
            data-testid="drip-health-stale-days"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E5E5] text-[#525252] text-[10px] font-bold uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-50"
            data-testid="drip-health-refresh"
          >
            <ArrowsClockwise size={11} weight="bold" />
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Bucket count tiles — also serve as tab triggers */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {BUCKETS.map(b => {
          const Icon = b.icon;
          const bk = data?.buckets?.[b.key];
          const active = activeTab === b.key;
          return (
            <button
              key={b.key}
              onClick={() => setActiveTab(b.key)}
              data-testid={`drip-health-tile-${b.key}`}
              className={`text-left border p-3 transition-all ${active ? "border-[#0055FF] bg-[#EFF6FF] -translate-y-0.5 shadow-sm" : "border-[#E5E5E5] bg-white hover:border-[#0055FF]"}`}
            >
              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${TONE_CLASSES[b.tone]}`}>
                <Icon size={10} weight="bold" />
                {bk?.label || b.key}
              </div>
              <div className="text-3xl font-black tracking-tight text-[#0A0A0A] mt-2">
                {bk?.count ?? "—"}
              </div>
              <p className="text-[10px] text-[#A3A3A3] mt-1 leading-snug line-clamp-2">
                {bk?.description}
              </p>
            </button>
          );
        })}
      </div>

      {/* Bucket detail */}
      <div className="border border-[#E5E5E5] bg-white">
        <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Info size={14} className="text-[#525252] shrink-0" />
            <span className="text-xs text-[#525252]" data-testid="drip-health-bucket-description">
              {bucket?.description || "Select a bucket above."}
            </span>
          </div>
          {/* Status legend. The Status column is a heat band, not an
              arbitrary label — saying so once here means the operator
              never has to guess what a colour means mid-demonstration. */}
          <div className="flex items-center gap-1.5 shrink-0" data-testid="drip-health-status-legend">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mr-0.5">
              Status
            </span>
            {["hot", "warm", "cool", "cold"].map(b => (
              <LeadStatusChip key={b} status={b} size="xs" />
            ))}
          </div>
        </div>

        {loading && (
          <div className="p-10 flex justify-center" data-testid="drip-health-loading">
            <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="p-10 text-center" data-testid="drip-health-empty">
            <Heart size={28} className="text-[#A3A3A3] mx-auto mb-2" />
            <p className="text-sm font-bold text-[#525252]">
              {bucket?.count === 0 ? `No leads in "${bucket?.label}".` : "No bucket selected."}
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <table className="w-full text-sm" data-testid="drip-health-table">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                <th className="px-4 py-2 text-left">Lead</th>
                <th className="px-4 py-2 text-left">Status</th>
                {activeTab === "paused" && <th className="px-4 py-2 text-left">Paused in</th>}
                {activeTab === "paused" && <th className="px-4 py-2 text-left">Since</th>}
                {activeTab === "lead_paused" && <th className="px-4 py-2 text-left">Reason</th>}
                {activeTab === "stale" && <th className="px-4 py-2 text-left">Last email</th>}
                <th className="px-4 py-2 text-left">Last action</th>
                <th className="px-4 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.lead_id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]">
                  <td className="px-4 py-3">
                    <div className="font-bold text-[#0A0A0A]">{it.company_name || "—"}</div>
                    <div className="text-[10px] text-[#A3A3A3]">
                      {it.contact_person} · {it.contact_email}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <LeadStatusChip status={it.lead_status} />
                  </td>
                  {activeTab === "paused" && (
                    <td className="px-4 py-3 text-xs text-[#525252]">{it.workflow_names || "—"}</td>
                  )}
                  {activeTab === "paused" && (
                    <td className="px-4 py-3 text-xs text-[#A3A3A3]">
                      {it.last_paused_at ? formatLocal(it.last_paused_at) : "—"}
                    </td>
                  )}
                  {activeTab === "lead_paused" && (
                    <td className="px-4 py-3 text-xs text-[#525252]">
                      {friendlyPauseReason(it.pause_reason) || <span className="text-[#A3A3A3] italic">no reason</span>}
                    </td>
                  )}
                  {activeTab === "stale" && (
                    <td className="px-4 py-3 text-xs text-[#A3A3A3]">
                      {it.last_sent_at ? formatLocal(it.last_sent_at) : <span className="italic">never</span>}
                    </td>
                  )}
                  <td className="px-4 py-3 text-xs text-[#A3A3A3]">
                    {it.last_action_date ? formatLocal(it.last_action_date) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/crm/leads/${it.lead_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-[#0055FF] hover:text-[#0055FF] whitespace-nowrap"
                      data-testid={`drip-health-open-${it.lead_id}`}
                    >
                      Open <ArrowRight size={10} weight="bold" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
